import type { Rule } from "@/apis/research/rules";
import {
  ACTION_MODE_LABELS,
  AI_SENTIMENT_LABELS,
  CHANNEL_LABELS,
  WHEN_TYPE_LABELS,
  type ActionMode,
  type AiSentiment,
  type Channel,
  type RuleDraft,
  type ThenActionId,
  type WhenType,
} from "@/components/ai-consumer-research/brand-moderator/rules/rule-builder-flow";

const CANONICAL_WHEN_TYPES = new Set<string>(Object.keys(WHEN_TYPE_LABELS));

const AI_TOPICS = new Set<string>(["Defect", "Comfort", "Size", "Quality", "Pricing", "Durability", "Style", "Influencer"]);

const CANONICAL_CHANNELS = new Set<string>(Object.keys(CHANNEL_LABELS));

function parseChannel(platform: string): Channel | undefined {
  return CANONICAL_CHANNELS.has(platform) ? (platform as Channel) : undefined;
}

const CANONICAL_ACTION_MODES = new Set<string>(Object.keys(ACTION_MODE_LABELS));

function parseActionMode(perform: string): ActionMode {
  return CANONICAL_ACTION_MODES.has(perform) ? (perform as ActionMode) : "needs_review";
}

const THEN_ACTION_IDS = new Set<string>(["flag_moderation_queue", "send_notification", "assign_priority_high"]);

// const CANONICAL_AI_SENTIMENTS = new Set<string>(Object.keys(AI_SENTIMENT_LABELS));

// function parseAiSentiment(param: string): AiSentiment {
//   return CANONICAL_AI_SENTIMENTS.has(param) ? (param as AiSentiment) : "positive";
// }

function parseWhenType(trigger: string): WhenType {
  return CANONICAL_WHEN_TYPES.has(trigger) ? (trigger as WhenType) : "ai_topic";
}

function clampStarRating(n: number): number {
  if (Number.isNaN(n) || n < 1) return 1;
  if (n > 5) return 5;
  return Math.round(n);
}

function confidenceToPercent(score: number): number {
  const pct = Math.round(score * 100);
  return Math.max(50, pct);
}

function splitKeywordCsv(s: string): string[] {
  return s
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Builds API `trigger_params` from the rule builder draft (inverse of `apiRuleToRuleDraft`). */
export function ruleDraftToTriggerParams(ruleDraft: RuleDraft): string[] {
  switch (ruleDraft.whenType) {
    case "star_rating":
      return [String(ruleDraft.whenStarRatingBelow)];
    case "keywords":
      return splitKeywordCsv(ruleDraft.whenKeywords);
    case "engagement_spike":
      return [String(ruleDraft.whenEngagementsAbove)];
    case "ai_sentiment":
      return [ruleDraft.whenAiSentiment];
    case "ai_topic":
      return [...ruleDraft.aiTopics];
    default: {
      const _exhaustive: never = ruleDraft.whenType;
      return _exhaustive;
    }
  }
}

/**
 * Maps a persisted moderation rule (API) into the flow builder draft shape.
 */
export function apiRuleToRuleDraft(rule: Rule): RuleDraft {
  const params = rule.trigger_params.map((p) => p.trim()).filter(Boolean);
  let whenType = parseWhenType(rule.trigger);

  let whenStarRatingBelow: RuleDraft["whenStarRatingBelow"] = 2;
  let whenKeywords = "";
  let whenEngagementsAbove = 5000;
  let whenAiSentiment: AiSentiment = "positive";
  let aiTopics: string[] = [];


  switch (whenType) {
    case "star_rating": {
      const n = Number.parseInt(params[0] ?? "2", 10);
      whenStarRatingBelow = clampStarRating(n);
      break;
    }
    case "keywords": {
      whenKeywords = params.length ? params.join(", ") : "";
      break;
    }
    case "engagement_spike": {
      const n = Number.parseInt(params[0] ?? "5000", 10);
      whenEngagementsAbove = Number.isFinite(n) ? n : 5000;
      break;
    }
    case "ai_sentiment": {
      whenAiSentiment = (params[0] ?? "positive") as AiSentiment;
      break;
    }
    case "ai_topic": {
      aiTopics = params.filter((p) => AI_TOPICS.has(p));
      whenKeywords = params.join(", ");
      break;
    }
    default:
      break;
  }

  const channels = rule.platform.map((p) => parseChannel(p)).filter((c): c is Channel => c !== undefined);

  const perform = parseActionMode(rule.perform);

  const thenActions = rule.actions?.filter((a): a is ThenActionId => THEN_ACTION_IDS.has(a));

  return {
    whenType,
    whenStarRatingBelow,
    whenKeywords,
    whenEngagementsAbove,
    whenAiSentiment,
    aiTopics,
    channels: channels.length ? channels : ["x"],
    actionMode: perform,
    autoRespondDowngradeToReviewBelowConfidence: confidenceToPercent(rule.perform_confidence_score),
    thenActions: thenActions?.length ? thenActions : ["flag_moderation_queue"],
  };
}

/** Human-readable WHEN line; matches the rule builder summary panel. */
export function summarizeRuleDraftWhen(ruleDraft: RuleDraft): string {
  switch (ruleDraft.whenType) {
    case "ai_topic":
      return ruleDraft.aiTopics.length ? `AI topic ∈ ${ruleDraft.aiTopics.join(", ")}` : "AI topic = (none)";
    case "star_rating":
      return `Star rating < ${ruleDraft.whenStarRatingBelow}`;
    case "keywords":
      return ruleDraft.whenKeywords.trim() ? `Keywords: ${ruleDraft.whenKeywords}` : "Keywords: (none)";
    case "engagement_spike":
      return `Engagements > ${ruleDraft.whenEngagementsAbove}`;
    case "ai_sentiment":
      return `AI sentiment = ${AI_SENTIMENT_LABELS[ruleDraft.whenAiSentiment]}`;
    default:
      return WHEN_TYPE_LABELS[ruleDraft.whenType] ?? ruleDraft.whenType;
  }
}

export function apiRuleWhenSummaryLine(rule: Rule): string {
  return summarizeRuleDraftWhen(apiRuleToRuleDraft(rule));
}
